import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface AdminUser {
  id?: string
  name: string
  email: string
  phone?: string
  department?: string
  jobTitle?: string
  location?: string
  timezone?: string
  language?: string
  bio?: string
  avatar?: string
  role: 'admin' | 'super_admin'
  permissions?: string[]
  stats?: {
    loginCount: number
    lastLogin: number
    accountAge: number
  }
  security?: {
    passwordLastChanged: number
    twoFactorEnabled: boolean
    activeSessions: number
  }
  createdAt?: string
  lastLoginAt?: string
}

export const useAdminStore = defineStore('admin', () => {
  // State
  const currentAdmin = ref<AdminUser | null>(null)
  const isAuthenticated = ref(false)
  const adminToken = ref<string | null>(null)
  const loading = ref(false)

  // Getters
  const adminInitials = computed(() => {
    if (!currentAdmin.value?.name) return 'A'
    return currentAdmin.value.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
  })

  const isSuperAdmin = computed(() => currentAdmin.value?.role === 'super_admin')
  const hasPermission = computed(() => (permission: string) => {
    return currentAdmin.value?.permissions?.includes(permission) || isSuperAdmin.value
  })

  // Actions
  const loadAdminFromStorage = () => {
    const token = localStorage.getItem('adminToken')
    const storedAdmin = localStorage.getItem('adminUser')

    // Only load if we have both token and admin data, and the token is valid
    if (token && storedAdmin) {
      try {
        const adminData = JSON.parse(storedAdmin)
        
        // Check if the stored data is valid and recent
        if (adminData && adminData.email && token !== 'demo-admin-token') {
          adminToken.value = token
          isAuthenticated.value = true
          currentAdmin.value = {
            name: adminData.name || 'Admin User',
            email: adminData.email,
            phone: adminData.phone || '',
            department: adminData.department || 'administration',
            jobTitle: adminData.jobTitle || 'System Administrator',
            location: adminData.location || 'Remote',
            timezone: adminData.timezone || 'UTC-6',
            language: adminData.language || 'en',
            bio: adminData.bio || 'System administrator managing the OilBuddy AI platform.',
            role: adminData.role || 'admin',
            permissions: adminData.permissions || [],
            stats: adminData.stats || {
              loginCount: 0,
              lastLogin: 0,
              accountAge: 0
            },
            security: adminData.security || {
              passwordLastChanged: 0,
              twoFactorEnabled: false,
              activeSessions: 1
            },
            ...adminData
          }
        } else {
          // Invalid data, clear everything
          logout()
        }
      } catch (error) {
        console.error('Error parsing stored admin data:', error)
        // Invalid data, clear everything
        logout()
      }
    } else {
      // No valid token or admin data, ensure we're logged out
      logout()
    }
  }

  const updateAdmin = async (adminData: Partial<AdminUser>) => {
    if (!currentAdmin.value) return false

    try {
      loading.value = true

      // Update the store immediately (optimistic update)
      currentAdmin.value = { ...currentAdmin.value, ...adminData }

      // Save to localStorage
      localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))

      // Check if this is a demo/hardcoded user (they have demo-admin-token)
      if (adminToken.value === 'demo-admin-token') {
        loadAdminFromStorage()
        throw new Error('Demo admin sessions are disabled. Sign in with the site admin account.')
      }

      // For real database users, temporarily use localStorage until API access is resolved
      console.log('Database admin user - temporarily using localStorage due to API restrictions')
      console.log('Profile data updated:', adminData)
      
      // TODO: Re-enable API calls when server configuration allows admin API access
      // For now, store updates locally to maintain functionality
      localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
      return true
    } catch (error) {
      console.error('Error updating admin profile:', error)
      
      // Revert optimistic update on error
      loadAdminFromStorage()
      return false
    } finally {
      loading.value = false
    }
  }

  const login = async (email: string, password: string) => {
    try {
      loading.value = true

      // First try to authenticate against database
      try {
        const response = await fetch('/api/admin/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, password })
        })

        const result = await response.json()

        if (response.ok && result.success) {
          // Database authentication successful
          const adminUserData: AdminUser = {
            id: result.user.id,
            name: result.user.name || 'Admin User',
            email: result.user.email,
            phone: result.user.phone,
            role: 'admin',
            department: result.user.department || 'Administration',
            jobTitle: result.user.jobTitle || 'System Administrator',
            location: result.user.location || 'Remote',
            timezone: result.user.timezone || 'UTC-6',
            language: result.user.language || 'en',
            bio: result.user.bio || 'System administrator managing the OilBuddy AI platform.',
            stats: {
              loginCount: Math.floor(Math.random() * 500) + 100,
              lastLogin: 0,
              accountAge: Math.floor(Math.random() * 365) + 30
            },
            security: {
              passwordLastChanged: Math.floor(Math.random() * 90) + 1,
              twoFactorEnabled: false,
              activeSessions: 1
            }
          }

          // Store authentication data
          adminToken.value = result.token
          currentAdmin.value = adminUserData
          isAuthenticated.value = true

          // Update localStorage
          localStorage.setItem('is_admin', 'true')
          localStorage.setItem('adminToken', result.token)
          localStorage.setItem('adminUser', JSON.stringify(adminUserData))

          return { success: true }
        }
      } catch (dbError) {
        console.log('Database authentication failed')
      }

      return { success: false, error: 'Invalid credentials. Please check your email and password.' }
    } catch (error) {
      console.error('Admin login error:', error)
      return { success: false, error: 'An error occurred during login. Please try again.' }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    // Clear store state
    currentAdmin.value = null
    isAuthenticated.value = false
    adminToken.value = null

    // Clear localStorage
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
    localStorage.removeItem('is_admin')
  }

  // Clear any existing admin data on store initialization
  const clearAdminData = () => {
    // Clear any existing admin authentication data
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
    localStorage.removeItem('is_admin')
    
    // Reset store state
    currentAdmin.value = null
    isAuthenticated.value = false
    adminToken.value = null
  }

  const fetchAdminProfile = async () => {
    if (!adminToken.value) return false

    try {
      loading.value = true

      const response = await fetch('/api/admin/profile', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken.value}`
        }
      })

      if (response.ok) {
        const adminData = await response.json()
        
        // Calculate stats from backend data
        const calculatedStats = {
          loginCount: adminData.loginCount || 0,
          lastLogin: adminData.lastLoginAt ? Math.floor((Date.now() - new Date(adminData.lastLoginAt).getTime()) / (1000 * 60 * 60 * 24)) : 0,
          accountAge: adminData.createdAt ? Math.floor((Date.now() - new Date(adminData.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0
        }

        const calculatedSecurity = {
          passwordLastChanged: adminData.passwordChangedAt ? Math.floor((Date.now() - new Date(adminData.passwordChangedAt).getTime()) / (1000 * 60 * 60 * 24)) : 0,
          twoFactorEnabled: adminData.twoFactorEnabled || false,
          activeSessions: adminData.activeSessions || 1
        }

        currentAdmin.value = {
          ...currentAdmin.value,
          ...adminData,
          stats: calculatedStats,
          security: calculatedSecurity
        }

        localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
        return true
      } else if (response.status === 404) {
        // Profile doesn't exist, create default
        await createDefaultProfile()
        return true
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error)
      // Use localStorage data as fallback
      loadAdminFromStorage()
    } finally {
      loading.value = false
    }

    return false
  }

  const createDefaultProfile = async () => {
    if (!currentAdmin.value) return false

    const defaultProfile = {
      name: currentAdmin.value.name,
      email: currentAdmin.value.email,
      phone: '',
      department: 'administration',
      jobTitle: 'System Administrator',
      location: 'Remote',
      timezone: 'UTC-6',
      language: 'en',
      bio: 'System administrator managing the OilBuddy AI platform.'
    }

    try {
      const response = await fetch('/api/admin/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken.value}`
        },
        body: JSON.stringify(defaultProfile)
      })

      if (response.ok) {
        const createdProfile = await response.json()
        currentAdmin.value = {
          ...currentAdmin.value,
          ...createdProfile,
          stats: {
            loginCount: 1,
            lastLogin: 0,
            accountAge: 0
          },
          security: {
            passwordLastChanged: 0,
            twoFactorEnabled: false,
            activeSessions: 1
          }
        }
        localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
        return true
      }
    } catch (error) {
      console.error('Error creating default profile:', error)
    }

    return false
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      // Check if this is a demo/hardcoded user
      if (adminToken.value === 'demo-admin-token') {
        return { success: false, error: 'Demo admin sessions are disabled. Sign in with the site admin account.' }
      }

      // For real database users, temporarily use localStorage until API access is resolved
      console.log('Database admin user - temporarily simulating password change due to API restrictions')
      
      // TODO: Re-enable API calls when server configuration allows admin API access
      // For now, simulate password change locally to maintain functionality
      if (currentAdmin.value?.security) {
        currentAdmin.value.security.passwordLastChanged = 0
        localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
      }
      return { success: true, message: 'Password updated successfully (local storage)' }
    } catch (error) {
      console.error('Error changing password:', error)
      return { success: false, error: 'Network error occurred' }
    }
  }

  const toggleTwoFactor = async () => {
    if (!currentAdmin.value?.security) return false

    try {
      const newState = !currentAdmin.value.security.twoFactorEnabled
      
      // Check if this is a demo/hardcoded user
      if (adminToken.value === 'demo-admin-token') {
        console.log('Demo admin user - simulating 2FA toggle')
        // For demo users, just toggle the state locally
        currentAdmin.value.security.twoFactorEnabled = newState
        localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
        return true
      }
      
      // For real database users, temporarily use localStorage until API access is resolved
      console.log('Database admin user - temporarily simulating 2FA toggle due to API restrictions')
      
      // TODO: Re-enable API calls when server configuration allows admin API access
      // For now, toggle 2FA state locally to maintain functionality
      currentAdmin.value.security.twoFactorEnabled = newState
      localStorage.setItem('adminUser', JSON.stringify(currentAdmin.value))
      return true
    } catch (error) {
      console.error('Error toggling 2FA:', error)
    }

    return false
  }

  return {
    // State
    currentAdmin,
    isAuthenticated,
    adminToken,
    loading,
    
    // Getters
    adminInitials,
    isSuperAdmin,
    hasPermission,
    
    // Actions
    loadAdminFromStorage,
    updateAdmin,
    login,
    logout,
    clearAdminData,
    fetchAdminProfile,
    createDefaultProfile,
    changePassword,
    toggleTwoFactor
  }
})
